import React, { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { Mic, Clock, MessageSquare, Trash2, MoreHorizontal } from 'lucide-react'
import { formatRelativeShort } from '../../utils/formatRelativeTime'
import type { ResearchItem } from './types'
import {
  ResearchRow,
  RowAccent,
  RowIconBadge,
  RowMain,
  RowTitleLine,
  RowTitle,
  RowTypeLabel,
  RowStatusLine,
  RowStatusItem,
  RowStatusSep,
  RowMetric,
  RowMetricNumber,
  RowMetricLabel,
  RowActions,
  RowActionButton,
  RowRecordDot,
  RowMoreButton,
} from './ResearchPageStyles'

interface InterviewCardProps {
  item: ResearchItem
  onDelete: (item: ResearchItem) => void
}

export const InterviewCard = React.memo(({ item, onDelete }: InterviewCardProps) => {
  const navigate = useNavigate()

  const handleCardClick = useCallback(() => {
    void navigate(`/interview/${item.id}/insights`)
  }, [item.id, navigate])

  const handleRecord = useCallback(() => {
    void navigate(`/interview/${item.id}/conduct`)
  }, [item.id, navigate])

  const moreMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'delete',
        icon: <Trash2 size={13} />,
        label: 'Delete interview',
        danger: true,
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation()
          onDelete(item)
        },
      },
    ],
    [item, onDelete]
  )

  return (
    <ResearchRow onClick={handleCardClick}>
      {/* Interviews don't have a live/closed status — accent stays invisible. */}
      <RowAccent $live={false} />
      <RowIconBadge>
        <Mic size={16} />
      </RowIconBadge>
      <RowMain>
        <RowTitleLine>
          <RowTitle title={item.label}>{item.label || 'Untitled interview'}</RowTitle>
          <RowTypeLabel>Interview</RowTypeLabel>
        </RowTitleLine>
        <RowStatusLine>
          <RowStatusItem>
            <MessageSquare size={11} />
            {item.questionCount} question{item.questionCount !== 1 ? 's' : ''}
          </RowStatusItem>
          <RowStatusSep />
          <RowStatusItem>
            <Clock size={11} />~{item.estimatedMinutes}m
          </RowStatusItem>
          <RowStatusSep />
          <RowStatusItem>Created {formatRelativeShort(item.createdAt, true)}</RowStatusItem>
        </RowStatusLine>
      </RowMain>
      <RowMetric>
        <RowMetricNumber>{item.respondentCount}</RowMetricNumber>
        <RowMetricLabel>interview{item.respondentCount !== 1 ? 's' : ''}</RowMetricLabel>
      </RowMetric>
      <RowActions onClick={(e) => e.stopPropagation()}>
        <RowActionButton type="button" onClick={handleRecord} title="Record an interview">
          <RowRecordDot />
          Record
        </RowActionButton>
        <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="bottomRight">
          <RowMoreButton type="button" title="More actions">
            <MoreHorizontal size={16} />
          </RowMoreButton>
        </Dropdown>
      </RowActions>
    </ResearchRow>
  )
})

InterviewCard.displayName = 'InterviewCard'
